import { accent } from '@/components/feature/solution/theme';

interface SceneLabel {
  icon: string;
  label: string;
}

interface ParkingHeroData {
  eyebrow: string;
  title: string;
  desc: string;
  chips: string[];
  primaryLabel: string;
  primaryHref: string;
  secondaryLabel: string;
  secondaryHref: string;
  image: string;
  sceneLabels: SceneLabel[];
}

interface ParkingHeroProps {
  data: ParkingHeroData;
}

export default function ParkingHero({ data }: ParkingHeroProps) {
  const a = accent('gold');

  return (
    <section className="relative w-full overflow-hidden bg-foreground-950 flex items-center min-h-[600px] md:min-h-[720px]">
      {/* 全屏背景图 */}
      <div className="absolute inset-0">
        <img
          src={data.image}
          alt="自由流停车全场景背景：高速公路、城市道路与停车场无感通行"
          title="自由流停车全场景背景"
          className="w-full h-full object-cover object-center"
        />
        {/* 暗色遮罩，保证文字可读 */}
        <div className="absolute inset-0 bg-foreground-950/50"></div>
        <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/60 to-foreground-950/10"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/85 via-transparent to-foreground-950/40"></div>
      </div>

      {/* 网格光效 */}
      <div
        className="absolute inset-0 opacity-[0.14] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(to right, rgba(255,255,255,0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.14) 1px, transparent 1px)',
          backgroundSize: '56px 56px',
        }}
      ></div>
      <div className="absolute -top-24 -left-24 w-80 h-80 md:w-[26rem] md:h-[26rem] rounded-full bg-secondary-500/20 blur-3xl animate-float-slow pointer-events-none"></div>
      <div
        className="absolute bottom-0 right-0 w-80 h-80 md:w-[30rem] md:h-[30rem] rounded-full bg-primary-500/15 blur-3xl animate-float-slow pointer-events-none"
        style={{ animationDelay: '2s' }}
      ></div>

      <div className="relative z-10 w-full px-4 md:px-6 py-28 md:py-36">
        <div className="max-w-[1200px] mx-auto w-full">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-background-50/10 border border-background-50/20 backdrop-blur">
              <span className={`w-2 h-2 rounded-full ${a.dot}`}></span>
              <span className="text-xs md:text-sm text-background-100 tracking-wide">{data.eyebrow}</span>
            </div>

            <h1 className="mt-6 font-heading font-bold text-background-50 text-3xl md:text-5xl leading-tight">
              {data.title}
            </h1>

            <p className="mt-5 text-sm md:text-base text-foreground-200 leading-relaxed max-w-2xl">{data.desc}</p>

            {/* 覆盖场景 */}
            <div className="mt-9 flex flex-wrap gap-2">
              {data.sceneLabels.map((s) => (
                <span
                  key={s.label}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-50/10 border border-background-50/20 backdrop-blur text-xs text-background-50 whitespace-nowrap"
                >
                  <i className={`${s.icon} text-secondary-300`}></i>
                  {s.label}
                </span>
              ))}
            </div>

            {/* 核心能力标签 */}
            <div className="mt-4 flex flex-wrap gap-2">
              {data.chips.map((tag, idx) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full text-xs text-background-100 bg-background-50/10 border border-background-50/15 whitespace-nowrap animate-float-slow"
                  style={{ animationDelay: `${idx * 0.4}s`, animationDuration: '7s' }}
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}